import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import {
  CircularProgress,
  Typography,
  TextField,
  MenuItem,
  Button,
  Divider,
  IconButton,
  Chip,
} from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ViewAgendaIcon from '@material-ui/icons/ViewAgenda';
import ViewListIcon from '@material-ui/icons/ViewList';
import PeopleIcon from '@material-ui/icons/People';
import SelectAllIcon from '@material-ui/icons/SelectAll';
import ClearIcon from '@material-ui/icons/Clear';
import axios from 'axios';
import { useAuth } from './auth/AuthContext';
import { SERVER_NAME } from './config';
import ThesisCard from './ThesisCard';
import ThesisDetail from './ThesisDetail';
import StoriesLinksPane from './StoriesLinksPane';
import { useNavigate, useLocation } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: "'Inter', sans-serif",
    color: '#ffffff',
    padding: '26px 18px 0px',
    maxWidth: 1480,
    margin: '0 auto',
    height: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'hidden',
    overflowY: 'auto',
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      padding: '4px 6px 0px',
    },
  },
  hero: {
    padding: '18px 18px 14px',
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.35)',
    background: 'radial-gradient(circle at 18% 18%, rgba(77,121,255,0.35), rgba(10,14,30,0.96))',
    boxShadow: '0 0 24px rgba(77,121,255,0.14)',
    marginBottom: 18,
    [theme.breakpoints.down('sm')]: {
      padding: '6px 8px 8px',
      marginBottom: 8,
    },
  },
  heroTitle: {
    fontSize: '2.15rem',
    fontWeight: 900,
    letterSpacing: -0.4,
    lineHeight: 1.1,
    textShadow: '0 0 16px rgba(77, 121, 255, 0.55)',
  },
  heroSubtitle: {
    marginTop: 6,
    color: '#cfd8ff',
    maxWidth: 900,
    lineHeight: 1.55,
    fontSize: 13,
  },
  controlsRow: {
    marginTop: 12,
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
  },
  controlField: {
    '& .MuiInputBase-root': {
      color: '#e5e7ff',
      background: 'rgba(5, 8, 20, 0.62)',
      borderRadius: 10,
      border: '1px solid rgba(255,255,255,0.10)',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiInputLabel-root': {
      color: '#9ea4d8',
    },
    [theme.breakpoints.down('sm')]: {
      '& .MuiInputBase-input': {
        fontSize: 11,
        padding: '6px 8px',
      },
      '& .MuiSelect-root': {
        fontSize: 11,
      },
    },
  },
  mainGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: 18,
    flex: '1 1 0%',
    minHeight: 0,
    overflow: 'visible',
    alignItems: 'stretch',
    alignContent: 'stretch',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
      gap: 12,
      overflow: 'visible',
    },
  },
  leftPanel: {
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    height: '100%',
    overflow: 'hidden',
  },
  feedPanel: {
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.22)',
    background: 'rgba(10, 14, 30, 0.82)',
    overflow: 'hidden',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 0%',
  },
  feedHeader: {
    padding: '10px 14px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  feedBody: {
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: 10,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    minHeight: 0,
    flex: '1 1 0%',
    touchAction: 'pan-y',
    overscrollBehaviorX: 'none',
    '& > *': {
      flexShrink: 0,
    },
  },
  thesisRow: {
    borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(5, 8, 20, 0.62)',
    padding: '8px 10px',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    '&:hover': { borderColor: 'rgba(77,121,255,0.4)' },
  },
  thesisRowSelected: {
    background: 'rgba(16, 20, 40, 0.96)',
    borderColor: 'rgba(158, 247, 197, 0.75)',
    boxShadow: '0 0 18px rgba(158,247,197,0.22)',
  },
  thesisRowTitle: {
    fontSize: 12,
    fontWeight: 900,
    color: '#e5e7ff',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  thesisRowSub: {
    fontSize: 11,
    color: '#cfd8ff',
    opacity: 0.9,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  thesisRowMeta: {
    fontSize: 11,
    color: '#9ea4d8',
    whiteSpace: 'nowrap',
  },
  miniDirChip: {
    height: 18,
    fontSize: 9,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontWeight: 900,
    color: '#fff',
  },
  perfStrip: {
    marginTop: 12,
    borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.10)',
    background: 'rgba(5, 8, 20, 0.62)',
    padding: 10,
    color: '#cfd8ff',
    fontSize: 12,
  },
  evidencePanel: {
    minHeight: 0,
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.45)',
    background: 'radial-gradient(circle at top left, rgba(77,121,255,0.22), rgba(5,8,20,0.98))',
    padding: 14,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    overflow: 'hidden',
  },
  evidenceHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  evidenceTitle: {
    fontSize: 14,
    fontWeight: 900,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
    color: '#e5e7ff',
  },
  evidenceSubtle: {
    fontSize: 12,
    color: '#9ea4d8',
  },
  evidenceSplitRoot: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  evidenceGraphWrap: {
    minHeight: 0,
    borderRadius: 10,
    overflow: 'hidden',
    backgroundColor: 'rgba(5,8,20,0.95)',
  },
  evidenceSplitter: {
    height: 10,
    flex: '0 0 auto',
    cursor: 'row-resize',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    userSelect: 'none',
    touchAction: 'none',
  },
  evidenceSplitterBar: {
    height: 4,
    width: '60%',
    borderRadius: 999,
    background: 'rgba(158, 196, 255, 0.35)',
  },
  evidenceListWrap: {
    minHeight: 0,
    borderRadius: 10,
    overflow: 'hidden',
    backgroundColor: 'black',
    display: 'flex',
    flexDirection: 'column',
  },
  evidenceListBody: {
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: 10,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    minHeight: 0,
  },
  evidenceRow: {
    borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.10)',
    background: 'rgba(5, 8, 20, 0.62)',
    padding: 10,
    cursor: 'pointer',
  },
  evidenceRowTitle: {
    fontSize: 12,
    fontWeight: 900,
    color: '#e5e7ff',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  evidenceRowMeta: {
    marginTop: 4,
    fontSize: 11,
    color: '#9ea4d8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  pipelineBox: {
    marginTop: 12,
    borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.10)',
    background: 'rgba(5, 8, 20, 0.62)',
    padding: 10,
  },
  pipelineRow: {
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.10)',
    background: 'rgba(10, 14, 30, 0.92)',
    padding: 10,
    cursor: 'default',
  },
  loadingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    gap: 12,
  },
}));

export default function ThesisPage({ openLogin }) {
  const classes = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { isAuthenticated, roles } = useAuth();
  const isPremiumPlus = useMemo(() => {
    const r = Array.isArray(roles) ? roles : [];
    return r.includes('premium') || r.includes('enterprise');
  }, [roles]);

  const [theses, setTheses] = useState([]);
  const [candidatesNew, setCandidatesNew] = useState([]);
  const [candidatesPromoted, setCandidatesPromoted] = useState([]);
  const [annotations, setAnnotations] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedThesisId, setSelectedThesisId] = useState(null);

  useEffect(() => {
    try {
      const search = location && typeof location.search === 'string' ? location.search : '';
      const params = new URLSearchParams(search);
      const id = params.get('thesisId') || params.get('id') || '';
      const tokenAddressParam = params.get('tokenAddress') || '';
      const tokenAddressState = location && location.state && location.state.tokenAddress ? location.state.tokenAddress : '';
      const tid = String(id || '').trim();
      if (tid) setSelectedThesisId(tid);
      const tokenAddress = String(tokenAddressParam || tokenAddressState || '').trim();
      if (tokenAddress) setSearchQuery(tokenAddress);
    } catch (e) {
    }
  }, [location]);

  const [statusFilter, setStatusFilter] = useState('all');
  const [directionFilter, setDirectionFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('conviction');

  const [pipelineOpen, setPipelineOpen] = useState(false);

  const readStoredViewMode = useCallback(() => {
    try {
      if (typeof window === 'undefined') return null;
      const raw = window.localStorage.getItem('thesesViewMode');
      const v = String(raw || '').trim();
      if (v === 'cards' || v === 'list') return v;
      return null;
    } catch (e) {
      return null;
    }
  }, []);

  const shortenAddressesInText = useCallback((text) => {
    const s = String(text || '');
    if (!s) return '';
    return s.replace(/0x[0-9a-fA-F]{40}/g, (m) => `${m.slice(0, 6)}...${m.slice(-4)}`);
  }, []);

  const [thesesViewMode, setThesesViewMode] = useState(() => readStoredViewMode() ?? 'list');
  const [groupedThesisIds, setGroupedThesisIds] = useState(() => new Set());
  const [groupModeEnabled, setGroupModeEnabled] = useState(false);

  const contribCacheRef = useRef(new Map());
  const [contribLoading, setContribLoading] = useState(false);
  const [contribError, setContribError] = useState('');
  const [contribStories, setContribStories] = useState([]);
  const [contribSeeds, setContribSeeds] = useState([]);
  const [contribKind, setContribKind] = useState('token');
  const [contribRabbitMeta, setContribRabbitMeta] = useState(null);

  const groupModeActive = !!groupModeEnabled && groupedThesisIds && groupedThesisIds.size > 0;

  const extractAddressesDeep = useCallback((x, out) => {
    if (!x) return;
    if (typeof x === 'string') {
      const m = x.match(/0x[0-9a-fA-F]{40}/g);
      if (m && m.length) m.forEach((a) => out.add(String(a).toLowerCase()));
      return;
    }
    if (Array.isArray(x)) {
      x.forEach((v) => extractAddressesDeep(v, out));
      return;
    }
    if (typeof x === 'object') {
      Object.keys(x).forEach((k) => extractAddressesDeep(x[k], out));
    }
  }, []);

  const buildStoryAddressSet = useCallback((story) => {
    const out = new Set();
    try {
      if (story && story.primary_token) {
        const a = String(story.primary_token).toLowerCase();
        if (/^0x[0-9a-f]{40}$/.test(a)) out.add(a);
      }
      if (story && story.meta && typeof story.meta === 'object') extractAddressesDeep(story.meta, out);
      const narr = (story && typeof story.ai_narrative === 'string' && story.ai_narrative.trim())
        ? story.ai_narrative
        : (story && story.subtitle ? String(story.subtitle) : '');
      if (narr) extractAddressesDeep(narr, out);
    } catch (e) {
    }
    return out;
  }, [extractAddressesDeep]);

  const looksLikeAddress = useCallback((s) => {
    const a = String(s || '').trim().toLowerCase();
    return /^0x[0-9a-f]{40}$/.test(a);
  }, []);

  const formatRelativeTime = useCallback((ts) => {
    try {
      if (!ts) return '';
      const d = new Date(String(ts).replace(' ', 'T'));
      if (isNaN(d.getTime())) return '';
      const diffSec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
      if (diffSec < 60) return `${diffSec}s ago`;
      const m = Math.floor(diffSec / 60);
      if (m < 60) return `${m}m ago`;
      const h = Math.floor(m / 60);
      if (h < 24) return `${h}h ago`;
      const days = Math.floor(h / 24);
      return `${days}d ago`;
    } catch (e) {
      return '';
    }
  }, []);

  const readStoredSplit = useCallback(() => {
    try {
      if (typeof window === 'undefined') return null;
      const raw = window.localStorage.getItem('thesisEvidenceSplit');
      const v = Number(raw);
      if (!Number.isFinite(v)) return null;
      if (v <= 0 || v >= 1) return null;
      return v;
    } catch (e) {
      return null;
    }
  }, []);

  const [evidenceSplitRatio, setEvidenceSplitRatio] = useState(() => readStoredSplit() ?? 0.5);
  const evidenceRootRef = useRef();
  const lastSplitClientYRef = useRef(null);

  const writeStoredSplit = useCallback((next) => {
    try {
      if (typeof window === 'undefined') return;
      window.localStorage.setItem('thesisEvidenceSplit', String(next));
    } catch (e) {
    }
  }, []);

  const applySplitFromPointer = useCallback((clientY) => {
    const el = evidenceRootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const totalH = rect.height || 0;
    if (!totalH) return;
    const y = clientY - rect.top;
    const next = Math.max(0.2, Math.min(0.8, y / totalH));
    setEvidenceSplitRatio(next);
    writeStoredSplit(next);
  }, [writeStoredSplit]);

  const startSplitDrag = useCallback((e) => {
    try {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
    } catch (err) {
    }

    const move = (ev) => {
      const y = ev && ev.clientY != null ? ev.clientY : null;
      if (y == null) return;
      lastSplitClientYRef.current = y;
      applySplitFromPointer(y);
    };

    const up = () => {
      const y = lastSplitClientYRef.current;
      if (y != null) applySplitFromPointer(y);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);

    const initialY = e && e.clientY != null ? e.clientY : null;
    if (initialY != null) {
      lastSplitClientYRef.current = initialY;
      applySplitFromPointer(initialY);
    }
  }, [applySplitFromPointer]);

  useEffect(() => {
    const resizeThesis = () => {
      const thesisContainer = document.getElementById('thesisContainer');
      if (!thesisContainer) return;

      const vv = (typeof window !== 'undefined' && window.visualViewport) ? window.visualViewport : null;
      const totalHeight = (vv && Number(vv.height)) ? Number(vv.height) : window.innerHeight;

      const e3dHeader = document.getElementById('e3dHeader');
      const adBannerContainer = document.getElementById('adBannerContainer');
      const premiumBannerContainer = document.getElementById('premiumBannerContainer');

      const otherHeight =
        (e3dHeader ? e3dHeader.clientHeight : 0) +
        (adBannerContainer ? adBannerContainer.clientHeight : 0) +
        (premiumBannerContainer ? premiumBannerContainer.clientHeight : 0);

      const availableHeight = totalHeight - otherHeight - 20;
      const clampedHeight = availableHeight > 0 ? availableHeight : totalHeight;

      thesisContainer.style.height = `${clampedHeight}px`;
      thesisContainer.style.maxHeight = `${clampedHeight}px`;
    };

    window.addEventListener('resize', resizeThesis);
    resizeThesis();
    return () => window.removeEventListener('resize', resizeThesis);
  }, []);

  const showLoginDialog = useCallback((e) => {
    try {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    } catch (err) {
    }

    if (typeof openLogin === 'function') {
      try { openLogin(); } catch (err2) {}
      return;
    }

    try {
      if (typeof window !== 'undefined' && typeof window.globalShowLogin === 'function') {
        window.globalShowLogin(true);
      }
      if (typeof window !== 'undefined' && typeof window.setGlobalShowLogin === 'function') {
        window.setGlobalShowLogin(true);
      }
      if (typeof document !== 'undefined') {
        document.dispatchEvent(new Event('force-login-dialog-render'));
      }
    } catch (e2) {
    }
  }, [openLogin]);

  const fetchTheses = useCallback(async () => {
    setError('');
    try {
      setLoading(true);
      const resp = await axios.get(`${SERVER_NAME}/theses`, {
        params: { status: statusFilter, limit: isAuthenticated ? 50 : 3 },
        withCredentials: true,
      });
      const next = resp && resp.data && Array.isArray(resp.data.theses) ? resp.data.theses : [];
      setTheses(next);
    } catch (e) {
      setError('Failed to fetch theses');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, statusFilter]);

  const fetchCandidates = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      if (isPremiumPlus) {
        const respNew = await axios.get(`${SERVER_NAME}/candidates`, {
          params: { status: 'new', limit: 10 },
          withCredentials: true,
        });
        setCandidatesNew(respNew && respNew.data && Array.isArray(respNew.data.candidates) ? respNew.data.candidates : []);
      } else {
        setCandidatesNew([]);
      }

      const respPromoted = await axios.get(`${SERVER_NAME}/candidates`, {
        params: { status: 'promoted', limit: 200 },
        withCredentials: true,
      });
      setCandidatesPromoted(respPromoted && respPromoted.data && Array.isArray(respPromoted.data.candidates) ? respPromoted.data.candidates : []);
    } catch (e) {
    }
  }, [isAuthenticated, isPremiumPlus]);

  const fetchAnnotations = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const resp = await axios.get(`${SERVER_NAME}/thesis/annotations`, { withCredentials: true });
      const next = resp && resp.data && resp.data.annotations && typeof resp.data.annotations === 'object' ? resp.data.annotations : {};
      setAnnotations(next);
    } catch (e) {
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchTheses();
    fetchCandidates();
    fetchAnnotations();

    const id = setInterval(() => {
      fetchTheses();
      fetchCandidates();
    }, 60000);

    return () => clearInterval(id);
  }, [fetchTheses, fetchCandidates, fetchAnnotations]);

  const promotedByThesisId = useMemo(() => {
    const map = new Map();
    (candidatesPromoted || []).forEach((c) => {
      const t = c && c.promoted_to ? String(c.promoted_to) : '';
      if (!t) return;
      map.set(t, c);
    });
    return map;
  }, [candidatesPromoted]);

  const filteredTheses = useMemo(() => {
    const q = String(searchQuery || '').trim().toLowerCase();
    const dir = String(directionFilter || 'all').toUpperCase();
    const status = String(statusFilter || 'all').toLowerCase();

    let res = Array.isArray(theses) ? theses.slice() : [];

    // Deduplicate by entity to avoid showing multiple theses for the same entity
    // (e.g., an older thesis with different direction).
    {
      const byEntity = new Map();
      for (const t of res) {
        const addr = String(t && t.entity_address ? t.entity_address : '').toLowerCase();
        if (!addr) continue;
        const prev = byEntity.get(addr);
        if (!prev) {
          byEntity.set(addr, t);
          continue;
        }
        const prevTs = new Date(prev.ts_updated || prev.ts_created || 0).getTime();
        const nextTs = new Date(t.ts_updated || t.ts_created || 0).getTime();
        if (nextTs >= prevTs) byEntity.set(addr, t);
      }
      res = Array.from(byEntity.values());
    }

    if (status !== 'all') {
      const statusList = status.split(',').map((s) => String(s || '').trim().toLowerCase()).filter(Boolean);
      if (statusList.length) {
        res = res.filter((t) => statusList.includes(String(t && t.status ? t.status : '').toLowerCase()));
      }
    }

    if (dir !== 'ALL') {
      res = res.filter((t) => String(t && t.direction ? t.direction : '').toUpperCase() === dir);
    }

    if (q) {
      res = res.filter((t) => {
        const sym = (t && t.entity_symbol) ? String(t.entity_symbol).toLowerCase() : '';
        const nm = (t && t.entity_name) ? String(t.entity_name).toLowerCase() : '';
        const addr = (t && t.entity_address) ? String(t.entity_address).toLowerCase() : '';
        const txt = (t && t.thesis_text) ? String(t.thesis_text).toLowerCase() : '';
        return sym.includes(q) || nm.includes(q) || addr.includes(q) || txt.includes(q);
      });
    }

    res = res.map((t) => {
      const id = t && t.id != null ? String(t.id) : '';
      const promoted = id ? promotedByThesisId.get(id) : null;
      const storyTypes = promoted && Array.isArray(promoted.story_types) ? promoted.story_types : null;
      if (storyTypes && !Array.isArray(t.story_types)) {
        return { ...t, story_types: storyTypes };
      }
      return t;
    });

    if (sortBy === 'recent') {
      res.sort((a, b) => new Date(b.ts_updated) - new Date(a.ts_updated));
    } else {
      res.sort((a, b) => (Number(b && b.conviction) || 0) - (Number(a && a.conviction) || 0));
    }

    return res;
  }, [theses, directionFilter, searchQuery, sortBy, promotedByThesisId]);

  const visibleTheses = useMemo(() => {
    const list = Array.isArray(filteredTheses) ? filteredTheses : [];
    if (isAuthenticated) return list;
    return list.slice(0, 3);
  }, [filteredTheses, isAuthenticated]);

  const [manualThesisOrder, setManualThesisOrder] = useState([]);

  useEffect(() => {
    const ids = (visibleTheses || [])
      .map((t) => (t && t.id != null ? String(t.id) : ''))
      .filter(Boolean);

    setManualThesisOrder((prev) => {
      const prevArr = Array.isArray(prev) ? prev : [];
      const idSet = new Set(ids);
      const next = prevArr.filter((id) => idSet.has(String(id)));
      const seen = new Set(next.map((id) => String(id)));
      ids.forEach((id) => {
        if (seen.has(id)) return;
        seen.add(id);
        next.push(id);
      });
      return next;
    });
  }, [visibleTheses]);

  const orderedVisibleTheses = useMemo(() => {
    const list = Array.isArray(visibleTheses) ? visibleTheses.slice() : [];
    const order = Array.isArray(manualThesisOrder) ? manualThesisOrder : [];
    if (!order.length) return list;
    const idxById = new Map(order.map((id, idx) => [String(id), idx]));
    return list.sort((a, b) => {
      const ia = idxById.has(String(a && a.id)) ? idxById.get(String(a && a.id)) : Number.MAX_SAFE_INTEGER;
      const ib = idxById.has(String(b && b.id)) ? idxById.get(String(b && b.id)) : Number.MAX_SAFE_INTEGER;
      if (ia !== ib) return ia - ib;
      return 0;
    });
  }, [visibleTheses, manualThesisOrder]);

  const selectedThesis = useMemo(() => {
    const id = selectedThesisId != null ? String(selectedThesisId) : '';
    return orderedVisibleTheses.find((t) => String(t && t.id) === id) || orderedVisibleTheses[0] || null;
  }, [orderedVisibleTheses, selectedThesisId]);

  useEffect(() => {
    if (!orderedVisibleTheses.length) {
      if (selectedThesisId !== null) setSelectedThesisId(null);
      return;
    }

    const exists = orderedVisibleTheses.some((t) => String(t && t.id) === String(selectedThesisId));
    if (!exists) setSelectedThesisId(String(orderedVisibleTheses[0].id));
  }, [orderedVisibleTheses, selectedThesisId]);

  const scrollThesisIntoView = useCallback((tid) => {
    const id = tid != null ? String(tid) : '';
    if (!id) return;
    try {
      requestAnimationFrame(() => {
        try {
          const el = document.getElementById(`thesis-item-${id}`);
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        } catch (err) {
        }
      });
    } catch (err) {
    }
  }, []);

  const moveSelectedThesis = useCallback((delta) => {
    const tid = selectedThesisId != null ? String(selectedThesisId) : '';
    if (!tid) return;

    setManualThesisOrder((prev) => {
      const arr = Array.isArray(prev) ? prev.slice() : [];
      const idx = arr.findIndex((id) => String(id) === tid);
      if (idx === -1) return arr;
      const nextIdx = idx + Number(delta || 0);
      if (nextIdx < 0 || nextIdx >= arr.length) return arr;
      const tmp = arr[idx];
      arr[idx] = arr[nextIdx];
      arr[nextIdx] = tmp;
      return arr;
    });

    scrollThesisIntoView(tid);
  }, [selectedThesisId, scrollThesisIntoView]);

  const stats = useMemo(() => {
    const all = Array.isArray(theses) ? theses : [];
    const active = all.filter((t) => t && t.status === 'active').length;
    const confirmed = all.filter((t) => t && t.status === 'confirmed').length;
    const invalidated = all.filter((t) => t && t.status === 'invalidated').length;
    const expired = all.filter((t) => t && t.status === 'expired').length;
    const avgConviction = all.length
      ? (all.reduce((sum, t) => sum + (Number(t && t.conviction) || 0), 0) / all.length).toFixed(2)
      : '—';
    return { active, confirmed, invalidated, expired, total: all.length, avgConviction };
  }, [theses]);

  const onSaveAnnotation = useCallback(async (thesisId, update) => {
    const tid = String(thesisId || '').trim();
    if (!tid) return;

    const resp = await axios.put(`${SERVER_NAME}/thesis/annotations/${encodeURIComponent(tid)}`, update || {}, { withCredentials: true });
    const a = resp && resp.data ? resp.data.annotation : null;
    if (!a || !a.thesisId) {
      await fetchAnnotations();
      return;
    }

    setAnnotations((prev) => ({ ...prev, [String(a.thesisId)]: a }));
  }, [fetchAnnotations]);

  const onOpenStory = useCallback((storyId) => {
    const sid = String(storyId || '').trim();
    if (!sid) return;
    navigate(`/stories?storyId=${encodeURIComponent(sid)}`, { state: { from: location && location.pathname ? location.pathname : '/thesis' } });
  }, [navigate, location]);

  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      window.localStorage.setItem('thesesViewMode', String(thesesViewMode || 'list'));
    } catch (e) {
    }
  }, [thesesViewMode]);

  useEffect(() => {
    const ids = new Set((visibleTheses || []).map((t) => (t && t.id != null ? String(t.id) : null)).filter(Boolean));
    setGroupedThesisIds((prev) => {
      const next = new Set();
      (prev || []).forEach((id) => {
        if (ids.has(String(id))) next.add(String(id));
      });
      if (groupModeEnabled && next.size === 0) {
        setGroupModeEnabled(false);
      }
      return next;
    });
  }, [visibleTheses, groupModeEnabled]);

  const toggleGroupThesis = useCallback((e, thesis) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    if (!thesis || thesis.id == null) return;
    const tid = String(thesis.id);
    setGroupedThesisIds((prev) => {
      const next = new Set(prev || []);
      if (next.has(tid)) next.delete(tid);
      else next.add(tid);
      return next;
    });
  }, []);

  const handleSelectAllGroupedTheses = useCallback(() => {
    const ids = (visibleTheses || []).map((t) => (t && t.id != null ? String(t.id) : null)).filter(Boolean);
    setGroupedThesisIds(new Set(ids));
  }, [visibleTheses]);

  const handleClearGroupedTheses = useCallback(() => {
    setGroupedThesisIds(new Set());
    setGroupModeEnabled(false);
  }, []);

  const effectiveStoryIds = useMemo(() => {
    if (!groupModeActive) {
      return Array.isArray(selectedThesis && selectedThesis.contributing_story_ids)
        ? selectedThesis.contributing_story_ids.map((x) => String(x || '').trim()).filter(Boolean)
        : [];
    }

    const ids = new Set();
    (visibleTheses || []).forEach((t) => {
      if (!t || t.id == null) return;
      const tid = String(t.id);
      if (!groupedThesisIds.has(tid)) return;
      const sids = Array.isArray(t.contributing_story_ids) ? t.contributing_story_ids : [];
      sids.forEach((sid) => {
        const s = String(sid || '').trim();
        if (s) ids.add(s);
      });
    });

    return Array.from(ids);
  }, [groupModeActive, groupedThesisIds, visibleTheses, selectedThesis]);

  const evidenceThesis = useMemo(() => {
    if (!groupModeActive) return selectedThesis;
    const key = `group:${Array.from(groupedThesisIds || []).sort().join(',')}`;
    return {
      id: key,
      entity_type: 'token',
      entity_address: '',
      contributing_story_ids: effectiveStoryIds,
    };
  }, [groupModeActive, groupedThesisIds, selectedThesis, effectiveStoryIds]);

  useEffect(() => {
    const thesis = evidenceThesis;
    const tid = thesis && thesis.id != null ? String(thesis.id) : '';
    if (!tid) {
      setContribLoading(false);
      setContribError('');
      setContribStories([]);
      setContribSeeds([]);
      setContribRabbitMeta(null);
      return;
    }

    const cached = contribCacheRef.current.get(tid);
    if (cached) {
      setContribStories(cached.stories || []);
      setContribSeeds(cached.seeds || []);
      setContribKind(cached.seedKind || 'token');
      setContribRabbitMeta((cached.rabbitMeta != null) ? cached.rabbitMeta : null);
      setContribLoading(false);
      setContribError('');
      return;
    }

    const ids = (Array.isArray(effectiveStoryIds) ? effectiveStoryIds : [])
      .map((x) => String(x || '').trim())
      .filter(Boolean)
      .slice(0, 120);
    if (!ids.length) {
      setContribLoading(false);
      setContribError('');
      setContribStories([]);
      setContribSeeds([]);
      setContribRabbitMeta(null);
      return;
    }

    let cancelled = false;
    const run = async () => {
      setContribLoading(true);
      setContribError('');
      try {
        const resp = await axios.post(`${SERVER_NAME}/stories/byIds`, { ids }, { withCredentials: true });
        if (cancelled) return;
        const rows = resp && resp.data && Array.isArray(resp.data) ? resp.data : [];

        const storyRows = rows.map((s) => {
          const addrSet = buildStoryAddressSet(s);
          return {
            story: s,
            addrSet,
            addrCount: addrSet.size,
          };
        });

        const normalizeSeed = (v) => {
          if (!v) return '';
          const s = String(v).trim().toLowerCase();
          if (!s) return '';
          return s.includes('.') ? s.split('.')[0] : s;
        };

        const groupedSeeds = [];
        const groupedSeen = new Set();
        const groupedRabbitMetas = [];
        storyRows.forEach((r) => {
          const s = r && r.story ? r.story : null;
          const primary = normalizeSeed(s && s.primary_token);
          if (primary && !groupedSeen.has(primary)) {
            groupedSeen.add(primary);
            groupedSeeds.push(primary);
          }
          if (primary && s && s.meta && typeof s.meta === 'object') {
            groupedRabbitMetas.push({ token: primary, meta: s.meta });
          }
        });

        const freq = new Map();
        storyRows.forEach((r) => {
          (r.addrSet ? Array.from(r.addrSet) : []).forEach((a) => {
            if (!a) return;
            freq.set(a, (freq.get(a) || 0) + 1);
          });
        });

        // Prefer grouped-mode behavior from /stories: build aggregated evidence
        // using rabbit meta across contributing stories (when present).
        const canUseRabbitGrouped = groupedRabbitMetas.length > 0 && groupedSeeds.length > 0;

        let seeds = [];
        let seedKind = 'token';
        let rabbitMeta = null;

        if (canUseRabbitGrouped) {
          seeds = groupedSeeds.slice(0, 25);
          rabbitMeta = groupedRabbitMetas;
          seedKind = 'token';
        } else {
          const baseSeeds = [];
          const baseSeen = new Set();

          const ent = thesis && thesis.entity_address ? String(thesis.entity_address).trim() : '';
          if (looksLikeAddress(ent)) {
            const a = ent.toLowerCase();
            baseSeeds.push(a);
            baseSeen.add(a);
          }

          storyRows.forEach((r) => {
            const pt = r && r.story && r.story.primary_token ? String(r.story.primary_token).trim().toLowerCase() : '';
            if (!looksLikeAddress(pt)) return;
            if (baseSeen.has(pt)) return;
            baseSeen.add(pt);
            baseSeeds.push(pt);
          });

          const extraSeeds = Array.from(freq.entries())
            .sort((a, b) => (b[1] || 0) - (a[1] || 0))
            .map((kv) => kv[0])
            .filter((a) => looksLikeAddress(a) && !baseSeen.has(a))
            .slice(0, 12);

          const MAX_TOTAL = 25;
          seeds = [...baseSeeds, ...extraSeeds].filter(Boolean).slice(0, MAX_TOTAL);

          seedKind = (() => {
            const t = thesis && thesis.entity_type ? String(thesis.entity_type).toLowerCase() : '';
            if (t === 'token') return 'token';
            if (t === 'address' || t === 'wallet' || t === 'mev_bot') return 'address';
            return 'token';
          })();
          rabbitMeta = null;
        }

        const storyList = storyRows
          .map((r) => ({
            id: r && r.story && r.story.id != null ? String(r.story.id) : '',
            story_type: r && r.story && r.story.story_type ? String(r.story.story_type) : '',
            title: r && r.story && r.story.title ? String(r.story.title) : '',
            ts_created: r && r.story && r.story.ts_created ? String(r.story.ts_created) : '',
            addrCount: r && typeof r.addrCount === 'number' ? r.addrCount : 0,
          }))
          .filter((r) => r && r.id);

        contribCacheRef.current.set(tid, {
          stories: storyList,
          seeds,
          seedKind,
          rabbitMeta,
        });

        setContribStories(storyList);
        setContribSeeds(seeds);
        setContribKind(seedKind);
        setContribRabbitMeta(rabbitMeta);
        setContribLoading(false);
        setContribError('');
      } catch (e) {
        if (cancelled) return;
        setContribLoading(false);
        setContribError('Failed to load contributing stories');
        setContribStories([]);
        setContribSeeds([]);
        setContribRabbitMeta(null);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [evidenceThesis, effectiveStoryIds, buildStoryAddressSet, looksLikeAddress]);

  const dirColors = useCallback((direction) => {
    const d = String(direction || '').toUpperCase();
    if (d === 'LONG') return { bg: '#2e7d32', label: 'LONG' };
    if (d === 'SHORT') return { bg: '#c62828', label: 'SHORT' };
    if (d === 'AVOID') return { bg: '#616161', label: 'AVOID' };
    return { bg: 'rgba(255,255,255,0.18)', label: d || '—' };
  }, []);

  const renderThesisCompactRow = useCallback((t) => {
    const tid = t && t.id != null ? String(t.id) : '';
    const isSelected = !!(selectedThesis && String(selectedThesis.id) === tid);
    const isGrouped = !!(groupedThesisIds && groupedThesisIds.has(tid));

    const dir = dirColors(t && t.direction);
    const sym = t && t.entity_symbol ? String(t.entity_symbol).trim() : '';
    const name = t && t.entity_name ? String(t.entity_name).trim() : '';
    const addr = t && t.entity_address ? String(t.entity_address).trim() : '';
    const label = sym ? (name ? `${sym} (${name})` : sym) : (name || addr || '—');
    const sub = t && t.thesis_text ? shortenAddressesInText(String(t.thesis_text).replace(/\s+/g, ' ').trim()) : '';
    const horizon = t && t.time_horizon ? String(t.time_horizon) : '';
    const conviction = Number(t && t.conviction != null ? t.conviction : 0);

    return (
      <div
        key={tid}
        id={`thesis-item-${tid}`}
        data-thesis-id={tid}
        className={`${classes.thesisRow} ${isSelected ? classes.thesisRowSelected : ''}`}
        onClick={() => setSelectedThesisId(tid)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setSelectedThesisId(tid);
          }
        }}
      >
        <Chip
          size="small"
          label={dir.label}
          className={classes.miniDirChip}
          style={{ backgroundColor: dir.bg }}
        />

        <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div className={classes.thesisRowTitle}>{label}</div>
          <div className={classes.thesisRowSub}>{sub || '—'}</div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Typography className={classes.thesisRowMeta}>{`${conviction.toFixed(2)}${horizon ? ` · ${horizon}` : ''}`}</Typography>
          <IconButton
            size="small"
            title={isGrouped ? 'Remove from group evidence' : 'Add to group evidence'}
            onClick={(e) => toggleGroupThesis(e, t)}
            style={{ padding: 4, color: isGrouped ? '#9ef7c5' : '#9ea4d8' }}
          >
            <PeopleIcon style={{ fontSize: 16 }} />
          </IconButton>
        </div>
      </div>
    );
  }, [classes, selectedThesis, groupedThesisIds, dirColors, toggleGroupThesis, shortenAddressesInText]);

  const renderThesisDetailWithGroupToggle = useCallback((t) => {
    const tid = t && t.id != null ? String(t.id) : '';
    const isGrouped = !!(groupedThesisIds && groupedThesisIds.has(tid));
    return (
      <div key={`detail-${tid}`} id={`thesis-item-${tid}`} data-thesis-id={tid}>
        <ThesisDetail
          thesis={t}
          annotation={annotations && t && t.id != null ? annotations[String(t.id)] : null}
          isAuthenticated={isAuthenticated}
          isPremiumPlus={isPremiumPlus}
          onSaveAnnotation={onSaveAnnotation}
          onOpenStory={onOpenStory}
          openLogin={openLogin}
          hideContributingStories={true}
          showGroupToggle={true}
          isGrouped={isGrouped}
          onToggleGroup={(e) => toggleGroupThesis(e, t)}
        />
      </div>
    );
  }, [groupedThesisIds, toggleGroupThesis, annotations, isAuthenticated, isPremiumPlus, onSaveAnnotation, onOpenStory, openLogin]);

  return (
    <div id="thesisContainer" className={classes.root}>
      <div className={classes.hero}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Typography className={classes.heroTitle} style={{ fontSize: isMobile ? '1.55rem' : '2.15rem' }}>
            Trade Theses
          </Typography>
          <Button
            variant="contained"
            size="small"
            style={{ backgroundColor: '#4d79ff', color: '#fff', textTransform: 'none', fontWeight: 800, borderRadius: 10, padding: '6px 14px', whiteSpace: 'nowrap' }}
            onClick={() => navigate('/thesis-guide')}
          >
            Guide
          </Button>
        </div>
        <Typography className={classes.heroSubtitle}>
          {isAuthenticated
            ? 'Agent-generated convergence theses bridging stories and automated execution.'
            : (
              <>
                Public preview: view up to 3 agent-generated theses.{" "}
                <a
                  href="#login"
                  style={{ color: '#9ef7c5', fontWeight: 900, cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={(e) => showLoginDialog(e)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      showLoginDialog(e);
                    }
                  }}
                >
                  Sign in
                </a>
                {' '}to unlock notes and more theses.
              </>
            )}
        </Typography>

        {!isAuthenticated ? (
          <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(5, 8, 20, 0.62)', color: '#cfd8ff', fontSize: 12, lineHeight: 1.5 }}>
            <div style={{ fontWeight: 900, color: '#e5e7ff', marginBottom: 4 }}>Preview mode</div>
            <div>
              {`Showing ${visibleTheses.length} of ${filteredTheses.length} theses. `}
              <a
                href="#login"
                style={{ color: '#9ef7c5', fontWeight: 900, cursor: 'pointer', textDecoration: 'underline' }}
                onClick={showLoginDialog}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    showLoginDialog(e);
                  }
                }}
              >
                Login
              </a>
              {' '}to see the full list and unlock notes + trade tools.
            </div>
          </div>
        ) : null}

        <div className={classes.controlsRow}>
          <TextField
            className={classes.controlField}
            select
            label="Status"
            variant="outlined"
            size="small"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ minWidth: 160 }}
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="confirmed">Confirmed</MenuItem>
            <MenuItem value="active,confirmed">Active + Confirmed</MenuItem>
            <MenuItem value="invalidated">Invalidated</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </TextField>

          <TextField
            className={classes.controlField}
            select
            label="Direction"
            variant="outlined"
            size="small"
            value={directionFilter}
            onChange={(e) => setDirectionFilter(e.target.value)}
            style={{ minWidth: 140 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="LONG">LONG</MenuItem>
            <MenuItem value="SHORT">SHORT</MenuItem>
            <MenuItem value="AVOID">AVOID</MenuItem>
          </TextField>

          <TextField
            className={classes.controlField}
            label="Search"
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ minWidth: isMobile ? 160 : 320, flex: '1 1 auto' }}
          />

          <TextField
            className={classes.controlField}
            select
            label="Sort"
            variant="outlined"
            size="small"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{ minWidth: 150 }}
          >
            <MenuItem value="conviction">Conviction</MenuItem>
            <MenuItem value="recent">Most recent</MenuItem>
          </TextField>

          <Button
            variant="outlined"
            size="small"
            style={{ borderColor: 'rgba(255,255,255,0.14)', color: '#cfd3ff', borderRadius: 10, textTransform: 'none' }}
            onClick={() => { fetchTheses(); fetchCandidates(); }}
          >
            Refresh
          </Button>
        </div>
      </div>

      <div className={classes.mainGrid}>
        <div className={classes.leftPanel}>
          <div className={classes.feedPanel}>
            <div className={classes.feedHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Typography style={{ fontSize: 12, fontWeight: 900, letterSpacing: 0.6, textTransform: 'uppercase', color: '#e5e7ff' }}>
                  Theses
                </Typography>
                <Typography style={{ fontSize: 12, color: '#9ea4d8' }}>{visibleTheses.length}</Typography>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, flex: 1 }}>
                <IconButton
                  size="small"
                  title="Move selected thesis up"
                  disabled={!selectedThesisId}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    moveSelectedThesis(-1);
                  }}
                  style={{ padding: 4, color: '#cfd3ff', opacity: selectedThesisId ? 1 : 0.35 }}
                >
                  <ChevronLeftIcon style={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  size="small"
                  title="Move selected thesis down"
                  disabled={!selectedThesisId}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    moveSelectedThesis(1);
                  }}
                  style={{ padding: 4, color: '#cfd3ff', opacity: selectedThesisId ? 1 : 0.35 }}
                >
                  <ChevronRightIcon style={{ fontSize: 18 }} />
                </IconButton>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <IconButton
                  size="small"
                  title="Card view"
                  onClick={() => setThesesViewMode('cards')}
                  style={{ padding: 4, color: thesesViewMode === 'cards' ? '#ffffff' : '#9ea4d8', border: thesesViewMode === 'cards' ? '1px solid rgba(158, 196, 255, 0.9)' : '1px solid transparent', borderRadius: 8 }}
                >
                  <ViewAgendaIcon style={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size="small"
                  title="List view"
                  onClick={() => setThesesViewMode('list')}
                  style={{ padding: 4, color: thesesViewMode === 'list' ? '#ffffff' : '#9ea4d8', border: thesesViewMode === 'list' ? '1px solid rgba(158, 196, 255, 0.9)' : '1px solid transparent', borderRadius: 8 }}
                >
                  <ViewListIcon style={{ fontSize: 16 }} />
                </IconButton>

                <IconButton
                  onClick={() => setGroupModeEnabled((v) => !v)}
                  title={groupModeEnabled ? 'Group mode: ON' : 'Group mode: OFF'}
                  size="small"
                  style={{ padding: 6, color: groupModeEnabled ? '#9ef7c5' : '#cfd3ff' }}
                >
                  <PeopleIcon style={{ fontSize: 18 }} />
                  {groupedThesisIds && groupedThesisIds.size > 0 && (
                    <span style={{ marginLeft: 6, fontSize: 12, color: '#fff' }}>
                      {groupedThesisIds.size}
                    </span>
                  )}
                </IconButton>

                <IconButton
                  onClick={handleSelectAllGroupedTheses}
                  disabled={(visibleTheses || []).length === 0}
                  title="Select all filtered theses"
                  size="small"
                  style={{ padding: 6, color: '#cfd3ff', opacity: ((visibleTheses || []).length === 0) ? 0.35 : 1 }}
                >
                  <SelectAllIcon style={{ fontSize: 18 }} />
                </IconButton>

                <IconButton
                  onClick={handleClearGroupedTheses}
                  disabled={(!groupedThesisIds || groupedThesisIds.size === 0) && !groupModeEnabled}
                  title="Reset group selection"
                  size="small"
                  style={{ padding: 6, color: '#cfd3ff', opacity: (((!groupedThesisIds || groupedThesisIds.size === 0) && !groupModeEnabled)) ? 0.35 : 1 }}
                >
                  <ClearIcon style={{ fontSize: 18 }} />
                </IconButton>
              </div>
            </div>

            <div className={classes.feedBody}>
              {loading ? (
                <div className={classes.loadingRow}>
                  <CircularProgress size={18} style={{ color: '#4d79ff' }} />
                  <Typography style={{ color: '#9ea4d8', fontSize: 12 }}>Loading…</Typography>
                </div>
              ) : error ? (
                <Typography style={{ color: '#ff7a7a', fontSize: 12 }}>{error}</Typography>
              ) : !visibleTheses.length ? (
                <Typography style={{ color: '#9ea4d8', fontSize: 12 }}>No theses found.</Typography>
              ) : (
                orderedVisibleTheses.map((t) => {
                  const isSelected = !!(selectedThesis && String(selectedThesis.id) === String(t && t.id));

                  if (thesesViewMode === 'list') {
                    if (isSelected) {
                      return renderThesisDetailWithGroupToggle(t);
                    }
                    return renderThesisCompactRow(t);
                  }

                  // cards
                  if (isSelected) {
                    return renderThesisDetailWithGroupToggle(t);
                  }
                  return (
                    <ThesisCard
                      key={String(t.id)}
                      thesis={t}
                      annotation={annotations && t && t.id != null ? annotations[String(t.id)] : null}
                      isSelected={false}
                      onClick={() => setSelectedThesisId(String(t.id))}
                      isPremiumPlus={isPremiumPlus}
                      showGroupToggle={true}
                      isGrouped={!!(groupedThesisIds && groupedThesisIds.has(String(t && t.id)))}
                      onToggleGroup={(e) => toggleGroupThesis(e, t)}
                    />
                  );
                })
              )}
            </div>
          </div>

          {isPremiumPlus ? (
            <div className={classes.pipelineBox}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <Typography style={{ fontSize: 12, fontWeight: 900, letterSpacing: 0.6, textTransform: 'uppercase', color: '#e5e7ff' }}>
                  Candidate Pipeline
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  style={{ borderColor: 'rgba(255,255,255,0.14)', color: '#cfd3ff', borderRadius: 10, textTransform: 'none' }}
                  onClick={() => setPipelineOpen((p) => !p)}
                >
                  {pipelineOpen ? 'Hide' : 'Show'}
                </Button>
              </div>

              {pipelineOpen ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
                  {!candidatesNew.length ? (
                    <Typography style={{ color: '#9ea4d8', fontSize: 12 }}>No candidates.</Typography>
                  ) : (
                    candidatesNew.map((c) => (
                      <div key={String(c.id)} className={classes.pipelineRow}>
                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                          <Typography style={{ color: '#e5e7ff', fontWeight: 900, fontSize: 13 }}>
                            {c && (c.entity_symbol || c.entity_name) ? `${c.entity_symbol || ''}${c.entity_name ? ` (${c.entity_name})` : ''}` : (c && c.entity_address ? String(c.entity_address) : '—')}
                          </Typography>
                          <Typography style={{ color: '#9ea4d8', fontSize: 12 }}>{`score ${Number(c && c.convergence_score || 0).toFixed(2)}`}</Typography>
                        </div>
                        <div style={{ marginTop: 6, color: '#cfd8ff', fontSize: 12, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                          <span>{`${Number(c && c.signal_count || 0)} signals`}</span>
                          <span>{c && c.direction_hint ? String(c.direction_hint) : ''}</span>
                          <span>{c && c.time_horizon ? String(c.time_horizon) : ''}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className={classes.perfStrip} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <span>{`Active: ${stats.active} | Confirmed: ${stats.confirmed} | Invalidated: ${stats.invalidated} | Expired: ${stats.expired} | Avg Conviction: ${stats.avgConviction}`}</span>
            {!isPremiumPlus && (
              <a
                href="/signup"
                style={{ color: '#7eb3ff', textDecoration: 'underline', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}
              >
                Upgrade for full thesis feed →
              </a>
            )}
          </div>
        </div>

        <div className={classes.evidencePanel}>
          <div className={classes.evidenceHeader}>
            <Typography className={classes.evidenceTitle}>Evidence</Typography>
            <Typography className={classes.evidenceSubtle}>
              {groupModeActive
                ? `${(groupedThesisIds && groupedThesisIds.size) ? groupedThesisIds.size : 0} theses`
                : (selectedThesis && selectedThesis.entity_address ? String(selectedThesis.entity_address).slice(0, 6) + '…' + String(selectedThesis.entity_address).slice(-4) : '')}
            </Typography>
          </div>

          <div className={classes.evidenceSplitRoot} ref={evidenceRootRef}>
            <div
              className={classes.evidenceGraphWrap}
              style={{ flex: `${evidenceSplitRatio} 1 0%`, flexBasis: 0 }}
            >
              {contribLoading ? (
                <div style={{ padding: 12, fontSize: 12, color: '#9ea4d8' }}>
                  <CircularProgress size={16} style={{ marginRight: 8 }} />
                  Loading evidence…
                </div>
              ) : contribError ? (
                <div style={{ padding: 12, fontSize: 12, color: '#ff7a7a' }}>{contribError}</div>
              ) : !contribSeeds.length ? (
                <div style={{ padding: 12, fontSize: 12, color: '#9ea4d8' }}>Select a thesis with contributing stories to view evidence.</div>
              ) : (
                <StoriesLinksPane
                  token={contribSeeds[0]}
                  tokens={contribSeeds}
                  seedKind={contribKind}
                  showGraph={true}
                  showList={false}
                  isMobile={isMobile}
                  onUpdatingChange={null}
                  rabbitMeta={contribRabbitMeta}
                />
              )}
            </div>

            <div className={classes.evidenceSplitter} onPointerDown={startSplitDrag} role="separator" aria-orientation="horizontal">
              <div className={classes.evidenceSplitterBar} />
            </div>

            <div
              className={classes.evidenceListWrap}
              style={{ flex: `${1 - evidenceSplitRatio} 1 0%`, flexBasis: 0 }}
            >
              <div style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography style={{ fontSize: 12, fontWeight: 900, letterSpacing: 0.6, textTransform: 'uppercase', color: '#e5e7ff' }}>
                  Contributing Stories
                </Typography>
                <Typography style={{ fontSize: 12, color: '#9ea4d8' }}>{contribStories.length}</Typography>
              </div>

              <div className={classes.evidenceListBody}>
                {contribLoading ? (
                  <div style={{ padding: 4, fontSize: 12, color: '#9ea4d8' }}>
                    <CircularProgress size={16} style={{ marginRight: 8 }} />
                    Loading…
                  </div>
                ) : contribError ? (
                  <div style={{ padding: 4, fontSize: 12, color: '#ff7a7a' }}>{contribError}</div>
                ) : !contribStories.length ? (
                  <div style={{ padding: 4, fontSize: 12, color: '#9ea4d8' }}>—</div>
                ) : (
                  contribStories.map((s) => (
                    <div
                      key={String(s.id)}
                      className={classes.evidenceRow}
                      onClick={() => onOpenStory(String(s.id))}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onOpenStory(String(s.id));
                        }
                      }}
                    >
                      <div className={classes.evidenceRowTitle}>{s.title || 'Untitled story'}</div>
                      <div className={classes.evidenceRowMeta}>
                        <span>{`${s.story_type || 'STORY'} · ${formatRelativeTime(s.ts_created)}`}</span>
                        <span>{`${Number(s.addrCount || 0)} addrs`}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ height: 18 }} />
      <Divider style={{ background: 'rgba(255,255,255,0.06)' }} />
      <div style={{ height: 14 }} />
    </div>
  );
}
